import * as XLSX from 'xlsx';
import type { ParseResult } from './parseResult';

export type { ParseResult } from './parseResult';

// Columnas esperadas (fila 1 del Excel): Codigo, Nombre, Cadena, Formato, Departamento,
// Provincia, Distrito, Zona, Direccion, Lat, Lng, RadioM
export interface PdvImportRow {
  code: string;
  name: string;
  chain: string;
  format: string;
  department: string;
  province: string;
  district: string;
  zone: string;
  address: string;
  lat: number | null;
  lng: number | null;
  /** Geocerca propia de la tienda en metros. null = usar el default (300 m). */
  radiusM: number | null;
  rowNumber: number;
}

const ALL_HEADERS = ['Codigo', 'Nombre', 'Cadena', 'Formato', 'Departamento', 'Provincia',
  'Distrito', 'Zona', 'Direccion', 'Lat', 'Lng', 'RadioM'] as const;
const REQUIRED_HEADERS = ['Codigo', 'Nombre'] as const;

function cellText(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

function parseCoord(v: unknown, label: string, rowErrors: string[]): number | null {
  const text = cellText(v);
  if (text === '') return null;
  const n = Number(text.replace(',', '.'));
  if (!Number.isFinite(n)) {
    rowErrors.push(`${label} no es numérico: "${text}"`);
    return null;
  }
  return n;
}

/** RadioM: entero positivo de metros. Vacío → null (la BD aplica el default 300). */
function parseRadius(v: unknown, rowErrors: string[]): number | null {
  const text = cellText(v);
  if (text === '') return null;
  const n = Number(text.replace(',', '.'));
  if (!Number.isFinite(n) || !Number.isInteger(n) || n <= 0) {
    rowErrors.push(`RadioM debe ser un entero positivo de metros: "${text}"`);
    return null;
  }
  return n;
}

export function parsePdvFile(data: ArrayBuffer): ParseResult<PdvImportRow> {
  const rows: PdvImportRow[] = [];
  const errors: { rowNumber: number; message: string }[] = [];

  const wb = XLSX.read(data, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return { rows, errors: [{ rowNumber: 0, message: 'El archivo no tiene hojas' }] };

  // header: 1 + blankrows: true → matriz cruda; el índice i corresponde a la fila Excel i+1
  const aoa = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[sheetName], {
    header: 1, defval: '', blankrows: true,
  });
  if (aoa.length === 0) return { rows, errors: [{ rowNumber: 0, message: 'La hoja está vacía' }] };

  const headerRow = (aoa[0] ?? []).map(cellText);
  const col: Record<string, number> = {};
  for (const h of ALL_HEADERS) {
    col[h] = headerRow.findIndex((t) => t.toLowerCase() === h.toLowerCase());
  }
  const missing = REQUIRED_HEADERS.filter((h) => col[h] === -1);
  if (missing.length > 0) {
    return { rows, errors: [{ rowNumber: 1, message: `Faltan columnas obligatorias: ${missing.join(', ')}` }] };
  }
  const get = (r: unknown[], h: string): unknown => (col[h] === -1 ? '' : r[col[h]]);

  /**
   * 🔴 LOS CÓDIGOS YA VISTOS EN ESTE ARCHIVO, para no dejar pasar un repetido.
   *
   * La importación manda todas las filas en UN solo `upsert`, que Postgres traduce a
   * `INSERT ... ON CONFLICT (code) DO UPDATE`. Ese comando **no puede tocar la misma fila dos
   * veces**: si el archivo trae dos filas con el mismo código, la segunda intentaría actualizar la
   * que la primera acaba de crear, y Postgres aborta el lote entero con
   * «ON CONFLICT DO UPDATE command cannot affect row a second time».
   *
   * Pasó el 17-set con un archivo de 7 PDV. La previsualización decía **«7 filas válidas · 0 filas
   * con error»** y después el servidor devolvía ese texto en inglés, sin decir qué fila era. Es
   * el mismo patrón que este proyecto ya tiene anotado como caro: **una comprobación que mira
   * cada elemento y nunca el conjunto**.
   *
   * Se guardan DOS mapas porque son dos problemas distintos:
   *  · el exacto rompe la importación;
   *  · el que solo cambia en mayúsculas NO la rompe —Postgres compara texto distinguiendo caja—
   *    y por eso es peor: entran dos PDV para el mismo mercado, con dos geocercas y las cifras
   *    partidas en dos, sin un solo error a la vista.
   */
  const vistos = new Map<string, number>();
  const vistosSinCaja = new Map<string, number>();

  for (let i = 1; i < aoa.length; i++) {
    const rowNumber = i + 1; // fila real del Excel
    const r = aoa[i] ?? [];
    if (r.every((c) => cellText(c) === '')) continue; // fila vacía → ignorada

    const rowErrors: string[] = [];
    const code = cellText(get(r, 'Codigo'));
    const name = cellText(get(r, 'Nombre'));
    if (code === '') rowErrors.push('Codigo es obligatorio');
    if (name === '') rowErrors.push('Nombre es obligatorio');

    if (code !== '') {
      const antes = vistos.get(code);
      const antesSinCaja = vistosSinCaja.get(code.toLowerCase());
      if (antes !== undefined) {
        rowErrors.push(
          `El código «${code}» ya está en la fila ${antes} de este archivo. Un mismo código no `
          + 'puede venir dos veces: la importación entra de una sola vez y la base rechazaría el '
          + 'lote completo. Deja una de las dos filas.',
        );
      } else if (antesSinCaja !== undefined) {
        rowErrors.push(
          `El código «${code}» es el mismo que el de la fila ${antesSinCaja} salvo por mayúsculas `
          + 'y minúsculas. Así entrarían DOS puntos de venta para el mismo sitio, cada uno con su '
          + 'geocerca y sus cifras. Escribe los dos igual, o cambia uno.',
        );
      }
    }
    const lat = parseCoord(get(r, 'Lat'), 'Lat', rowErrors);
    const lng = parseCoord(get(r, 'Lng'), 'Lng', rowErrors);
    const radiusM = parseRadius(get(r, 'RadioM'), rowErrors);

    if (rowErrors.length > 0) {
      // La fila con error NO entra a `rows`, que es lo que la pantalla importa. Dejarla adentro
      // con el error solo anotado al costado haría que la previsualización dijera «7 válidas · 1
      // error» y el `upsert` reventara igual: el aviso serviría para nada.
      errors.push({ rowNumber, message: rowErrors.join('; ') });
      continue;
    }
    vistos.set(code, rowNumber);
    vistosSinCaja.set(code.toLowerCase(), rowNumber);
    rows.push({
      code, name,
      chain: cellText(get(r, 'Cadena')),
      format: cellText(get(r, 'Formato')),
      department: cellText(get(r, 'Departamento')),
      province: cellText(get(r, 'Provincia')),
      district: cellText(get(r, 'Distrito')),
      zone: cellText(get(r, 'Zona')),
      address: cellText(get(r, 'Direccion')),
      lat, lng, radiusM, rowNumber,
    });
  }
  return { rows, errors };
}
